import { useEffect } from 'react';
import { onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { useAuthStore } from '@/stores/authStore';
import type { Owner } from '@/types/owner';

export function useAuthSubscription(): void {
  useEffect(() => {
    let unsubOwner: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (unsubOwner) {
        unsubOwner();
        unsubOwner = null;
      }

      if (!user) {
        useAuthStore.setState({
          user: null,
          owner: null,
          isAdmin: false,
          loading: false,
        });
        return;
      }

      useAuthStore.setState({ user, loading: true });

      try {
        const token = await getIdTokenResult(user);
        useAuthStore.setState({ isAdmin: token.claims.admin === true });
      } catch (err) {
        console.warn('Failed to read ID token claims', err);
      }

      unsubOwner = onSnapshot(
        doc(db, 'owners', user.uid),
        (snap) => {
          useAuthStore.setState({
            owner: snap.exists() ? (snap.data() as Owner) : null,
            loading: false,
          });
        },
        (err) => {
          console.warn('Owner snapshot error', err);
          useAuthStore.setState({ loading: false });
        },
      );
    });

    return () => {
      unsubAuth();
      if (unsubOwner) unsubOwner();
    };
  }, []);
}
